import React, { createContext, useContext, useEffect, useState } from "react";
import { sendRequest } from "../utils/api";

// Function to check internet via /ping endpoint
async function checkInternetConnection() {
    try {
        const authToken = localStorage.getItem("authToken");
        if (authToken) await sendRequest("GET", "/ping", false);
        return true;
    
    } catch {
        return false;
    }
}

type NetworkContextType = {
    isOnline: boolean;
};

const NetworkContext = createContext<NetworkContextType>({ isOnline: true });

export const NetworkProvider: React.FC<{ interval?: number; children: React.ReactNode }> = ({
    interval = 3000,
    children,
}) => {
    const [isOnline, setIsOnline] = useState<boolean>(
        localStorage.getItem("isOnline") === "true" || navigator.onLine
    );

    useEffect(() => {
        const updateStatus = (status: boolean) => {
            setIsOnline(status);
            localStorage.setItem("isOnline", status ? "true" : "false");
        };

        const checkConnection = async () => {
            const status = await checkInternetConnection();
            updateStatus(status);
        };

        // Initial check
        checkConnection();

        // Recheck every {interval} ms
        const timer = setInterval(checkConnection, interval);

        // Listen to browser online/offline events
        const handleOnline = () => updateStatus(true);
        const handleOffline = () => updateStatus(false);

        window.addEventListener("online", handleOnline);
        window.addEventListener("offline", handleOffline);

        return () => {
            clearInterval(timer);
            window.removeEventListener("online", handleOnline);
            window.removeEventListener("offline", handleOffline);
        };
    }, [interval]);

    return (
        <NetworkContext.Provider value={{ isOnline }}>
            {children}

            {/* Offline Banner */}
            {!isOnline && (
                <div style={{
                    position: "fixed",
                    bottom: 0,             // <-- change from bottom to top
                    left: 0,            // make sure it spans full width
                    width: "100%",
                    background: "#ffcccc",
                    textAlign: "center",
                    padding: "8px",
                    color: "#900",
                    fontWeight: "bold",
                    zIndex: 1000,
                }}>
                    ⚠️ You’re currently offline. Some features may be limited.
                </div>

            )}
        </NetworkContext.Provider>
    );
};

export const useNetwork = () => useContext(NetworkContext);
